import { useMemo } from 'react';
import { DurationValue } from '../DurationSlider';

export interface BoostPricingInfo {
  freeBoostsRemaining: number;
  freeBoostsNeeded: number;
  pointsNeeded: number;
  canAfford: boolean;
  needsMorePoints: boolean;
  totalCost: string;
}

export interface UseBoostPricingParams {
  selectedDuration: DurationValue;
  freeBoostsRemaining: number;
  freeBoostsAllowed: number;
  pointsBalance: number;
  boostPointsPrice?: number;
}

export const useBoostPricing = ({
  selectedDuration,
  freeBoostsRemaining,
  freeBoostsAllowed,
  pointsBalance,
  boostPointsPrice = 250,
}: UseBoostPricingParams): BoostPricingInfo => {
  return useMemo(() => {
    const totalBoostsNeeded = Math.ceil(selectedDuration / 2); // Total boosts needed for the duration
    // Use as many free boosts as possible for the duration
    // Each free boost covers 2 days (48 hours)
    // We can use up to the total free boosts allowed, but only if we have remaining ones
    const freeBoostsNeeded =
      freeBoostsRemaining > 0
        ? Math.min(
            totalBoostsNeeded, // Need this many boosts
            freeBoostsRemaining // But not more than what's allowed
          )
        : 0; // No free boosts if none remaining

    // Calculate remaining duration that needs to be covered by points
    const paidBoostsNeeded = totalBoostsNeeded - freeBoostsNeeded;
    const pointsNeeded = paidBoostsNeeded * boostPointsPrice;

    const canAfford = pointsBalance >= pointsNeeded;
    const needsMorePoints = pointsNeeded > 0 && !canAfford;

    const totalCost = `${
      freeBoostsNeeded > 0 ? `${freeBoostsNeeded} free boost${freeBoostsNeeded > 1 ? 's' : ''}` : ''
    }${freeBoostsNeeded > 0 && pointsNeeded > 0 ? ' + ' : ''}${
      pointsNeeded > 0 ? `${pointsNeeded} points` : ''
    }`;

    return {
      freeBoostsRemaining,
      freeBoostsNeeded,
      pointsNeeded,
      canAfford,
      needsMorePoints,
      totalCost,
    };
  }, [selectedDuration, freeBoostsRemaining, pointsBalance, boostPointsPrice]);
};
